/**
 * @file: The media types that a playlist item can point at
 *
 * A playlist item points at one row in one of three tables. The type string is
 * in the database row and in every client payload, so the server and the client
 * must use the same three strings. This module is the one place that lists them,
 * and the one place for the rule that gives a file media its play time.
 */

const MEDIA_TYPES = Object.freeze(['file', 'social', 'serviceAnnouncement'])

/**
 * Collect the distinct media ids of each type.
 *
 * A playlist repeats a media on purpose, so the same id appears many times. The
 * caller then reads or checks each distinct id once.
 *
 * An item with an unknown type is left out. A read path must still show the rows
 * that it has. The caller decides if an unknown type is an error.
 *
 * @param {Array} items - Items with a mediaType and a mediaId
 * @returns {object} One Set of ids for each known media type
 */
function distinctMediaIdsByType (items) {
  const groups = Object.fromEntries(MEDIA_TYPES.map(mediaType => [mediaType, new Set()]))

  items.forEach(item => {
    if (item && groups[item.mediaType]) {
      groups[item.mediaType].add(item.mediaId)
    }
  })

  return groups
}

const STILL_MS = 10000

/**
 * How long a file media plays. An image plays for ten seconds. A video plays
 * for the duration of its asset. The first asset that can say wins, in the
 * order the assets are stored. Null when no asset can say, and the server then
 * refuses the media.
 *
 * The server has the same rule in filemedia/getDuration. It is here so the
 * client shows the same time the server stores.
 */
function playbackDurationMs (assets = []) {
  const hit = assets.find(asset => asset.type === 'image' || (asset.type === 'video' && asset.duration))
  if (!hit) return null
  return hit.type === 'image' ? STILL_MS : hit.duration
}

/**
 * A social item plays one post for each ten seconds of its duration, and the
 * player takes whole posts only. Twelve posts is the most one line offers; a
 * longer social item can still come in from the scene page, and it is shown as
 * the posts it plays.
 */
const MAX_POSTS = 12

function postsOf (durationMs) {
  return Math.max(0, Math.floor((durationMs || 0) / STILL_MS))
}

function durationForPosts (posts) {
  const whole = Math.min(MAX_POSTS, Math.max(1, Math.floor(posts) || 1))
  return whole * STILL_MS
}

/**
 * What each media type can do. A file has assets, one per orientation, so it
 * can be missing on a scene and it has a measured length. A social source
 * plays posts. An announcement is one still. The client and the server read
 * this table instead of testing the type strings where they need them.
 *
 * Every type also holds labels. The label table has one through table per type.
 * `labelNamespace` is the name the medialabel transformers use for that through
 * table. `labelIdField` is the field of a row of it that names the media, which
 * a page reads when the server reports a change of the row.
 */
const MEDIA_TRAITS = Object.freeze({
  file: Object.freeze({
    hasAssets: true,
    playsPosts: false,
    labelNamespace: 'filemedias',
    labelIdField: 'filemediaId'
  }),
  social: Object.freeze({
    hasAssets: false,
    playsPosts: true,
    labelNamespace: 'socialmediasources',
    labelIdField: 'socialmediasourceId'
  }),
  serviceAnnouncement: Object.freeze({
    hasAssets: false,
    playsPosts: false,
    labelNamespace: 'serviceannouncements',
    labelIdField: 'serviceannouncementId'
  })
})

const hasAssets = mediaType => Boolean(MEDIA_TRAITS[mediaType] && MEDIA_TRAITS[mediaType].hasAssets)
const playsPosts = mediaType => Boolean(MEDIA_TRAITS[mediaType] && MEDIA_TRAITS[mediaType].playsPosts)

/**
 * The namespace a media type writes its labels under.
 *
 * @param {string} mediaType - One of MEDIA_TYPES
 * @returns {string|null} The namespace, or null for a type that is not known
 */
const labelNamespaceOf = mediaType =>
  (MEDIA_TRAITS[mediaType] && MEDIA_TRAITS[mediaType].labelNamespace) || null

/**
 * The traits of every media type, in the order of MEDIA_TYPES, each with its
 * own type name. A caller that walks the label namespaces reads this.
 *
 * @returns {Array} One entry per media type
 */
const mediaTraitsList = () =>
  MEDIA_TYPES.map(mediaType => Object.freeze({ mediaType, ...MEDIA_TRAITS[mediaType] }))

/**
 * The play time a media gets when it enters a playlist. A file plays by its
 * assets. A social source and an announcement play one still, and the operator
 * raises a social source to more posts afterwards. Null when a file has no
 * asset that can say.
 */
function defaultDurationMs (mediaType, assets) {
  return hasAssets(mediaType) ? playbackDurationMs(assets) : STILL_MS
}

/** The prefix a social source type shows in front of its filter. */
const SOCIAL_PREFIX = Object.freeze({
  InstagramHashtag: '#',
  InstagramMention: '@'
})

/**
 * The display name of a social source: its type's prefix and its filter. The
 * listing on the server and the catalogue on the client show the same name.
 * A filter from the source often has the prefix already, as in
 * `@roskildefestival Mention`, so the prefix is added only when it is missing.
 */
function socialSourceName (source) {
  const prefix = SOCIAL_PREFIX[source.type] || ''
  const filter = source.filter || ''
  return filter.startsWith(prefix) ? filter : `${prefix}${filter}`
}

export {
  MEDIA_TYPES,
  MEDIA_TRAITS,
  STILL_MS,
  MAX_POSTS,
  distinctMediaIdsByType,
  hasAssets,
  playsPosts,
  labelNamespaceOf,
  mediaTraitsList,
  playbackDurationMs,
  defaultDurationMs,
  postsOf,
  durationForPosts,
  socialSourceName
}
